import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

const TITLE = "Menopause Age Calculator: Stage & Onset Estimate";
const DESCRIPTION =
  "Free menopause age calculator. Estimate your menopause stage and likely onset age using clinical benchmarks inspired by the Greene Climacteric Scale.";
const CANONICAL = "https://knowmeno.com/guide/menopause-age-calculator";
const OG_IMAGE = "https://knowmeno.com/og-image.png?v=2";
const LAST_REVIEWED = new Date().toISOString().slice(0, 10);

const PLUM = "#2D0E43";
const PLUM_DEEP = "#4B1F4F";
const CORAL = "#F34E54";
const INK = "#231235";
const MUTED = "#5C4A66";

const faqItems: Array<{ q: string; a: string }> = [
  {
    q: "How accurate is a menopause age calculator?",
    a: "A calculator like this gives an educated estimate, not a diagnosis. It combines well-established clinical signals — your age, cycle pattern, family history, and symptoms scored on a Greene-Climacteric-style framework — to suggest where you likely sit in the menopause transition. Hormones fluctuate and life circumstances vary, so use the result as a starting point for a conversation with your clinician.",
  },
  {
    q: "What is the Greene Climacteric Scale?",
    a: "The Greene Climacteric Scale is a validated 21-item questionnaire developed by Dr. John Greene that measures menopause symptom severity across psychological, somatic, vasomotor, and sexual domains. It's widely used in research and clinical practice to track symptom burden over time.",
  },
  {
    q: "At what age does menopause usually start?",
    a: "The average age of natural menopause is around 51 to 52, with most women reaching it between 45 and 55. Perimenopause — the transition phase with symptoms — typically begins 4 to 8 years earlier. Menopause before age 40 is considered premature and deserves specialized care.",
  },
  {
    q: "Can I predict my exact menopause age?",
    a: "No tool can predict an exact date. The strongest single predictor is your mother's age at menopause, followed by smoking status (smokers reach menopause ~1–2 years earlier on average) and certain medical history. This calculator weighs those factors to produce a likely range, not a fixed date.",
  },
  {
    q: "What does my symptom score mean?",
    a: "Higher scores indicate a heavier symptom burden across the Greene domains (vasomotor, psychological, somatic, and sexual). A higher score combined with cycle changes makes perimenopause more likely. A low score in your 40s doesn't rule out the transition — many women have minimal symptoms early on.",
  },
];

export const Route = createFileRoute("/guide/menopause-age-calculator")({
  head: () => ({
    meta: [
      { title: `${TITLE} | KnowMeno` },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: CANONICAL },
      { property: "og:site_name", content: "KnowMeno" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      { rel: "alternate", hrefLang: "en", href: CANONICAL },
      { rel: "alternate", hrefLang: "x-default", href: CANONICAL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: TITLE,
          description: DESCRIPTION,
          url: CANONICAL,
          inLanguage: "en",
          lastReviewed: LAST_REVIEWED,
          publisher: { "@type": "Organization", name: "KnowMeno" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://knowmeno.com/" },
            { "@type": "ListItem", position: 2, name: "Menopause Guide", item: "https://knowmeno.com/menopause-guide" },
            { "@type": "ListItem", position: 3, name: "Menopause Age Calculator", item: CANONICAL },
          ],
        }),
      },
    ],
  }),
  component: MenopauseAgeCalculatorPage,
});

// ---------- styles ----------
const h2Style: React.CSSProperties = {
  fontFamily: "'Gloock', serif",
  fontSize: "clamp(1.5rem, 3.2vw, 1.95rem)",
  color: PLUM,
  marginTop: 48,
  marginBottom: 14,
  lineHeight: 1.2,
};

const h3Style: React.CSSProperties = {
  fontFamily: "'Gloock', serif",
  fontSize: "clamp(1.1rem, 2.2vw, 1.3rem)",
  color: PLUM_DEEP,
  marginTop: 24,
  marginBottom: 8,
  lineHeight: 1.3,
};

const pStyle: React.CSSProperties = {
  fontFamily: "'Lora', Georgia, serif",
  fontSize: "1.0625rem",
  lineHeight: 1.75,
  color: INK,
  margin: "0 0 14px",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Lora', serif",
  fontSize: 15,
  color: PLUM_DEEP,
  fontWeight: 600,
  display: "block",
  marginBottom: 8,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid rgba(75,31,79,0.2)",
  background: "#fff",
  fontFamily: "'Lora', serif",
  fontSize: 16,
  color: INK,
  boxSizing: "border-box",
};

const selectStyle: React.CSSProperties = { ...inputStyle, appearance: "auto" };

const fieldGroup: React.CSSProperties = { marginBottom: 18 };

// ---------- Greene-inspired symptom items ----------
type SymptomGroup = "Vasomotor" | "Psychological" | "Somatic" | "Sexual";
const SYMPTOMS: Array<{ id: string; label: string; group: SymptomGroup }> = [
  { id: "hot_flashes", label: "Hot flashes", group: "Vasomotor" },
  { id: "night_sweats", label: "Night sweats", group: "Vasomotor" },
  { id: "heart_pounding", label: "Heart beating quickly or strongly", group: "Vasomotor" },
  { id: "anxiety", label: "Feeling tense or anxious", group: "Psychological" },
  { id: "irritability", label: "Irritability", group: "Psychological" },
  { id: "low_mood", label: "Low mood or feeling unhappy", group: "Psychological" },
  { id: "concentration", label: "Difficulty concentrating", group: "Psychological" },
  { id: "sleep", label: "Trouble sleeping", group: "Psychological" },
  { id: "fatigue", label: "Tiredness or lack of energy", group: "Somatic" },
  { id: "headaches", label: "Headaches", group: "Somatic" },
  { id: "muscle_joint", label: "Muscle or joint aches", group: "Somatic" },
  { id: "dizziness", label: "Dizziness or feeling faint", group: "Somatic" },
  { id: "skin_crawling", label: "Pins and needles or skin crawling", group: "Somatic" },
  { id: "vaginal_dryness", label: "Vaginal dryness", group: "Sexual" },
  { id: "low_libido", label: "Loss of interest in sex", group: "Sexual" },
];

const SEVERITY_LABELS = ["None", "Mild", "Moderate", "Severe"];

type CycleRegularity = "regular" | "slightly_irregular" | "very_irregular" | "skipped_months" | "stopped_under_12" | "stopped_12_plus";

interface FormState {
  age: string;
  motherAge: string;
  smoking: "never" | "former" | "current";
  surgicalHistory: "none" | "hysterectomy" | "ovaries_removed" | "chemo";
  cycle: CycleRegularity;
  monthsSincePeriod: string;
  scores: Record<string, number>;
}

function calculate(form: FormState) {
  const age = Number(form.age) || 0;
  const motherAge = Number(form.motherAge) || 0;
  const monthsSince = Number(form.monthsSincePeriod) || 0;

  // Greene-style score (0–45 with 15 items × 0–3)
  const symptomScore = SYMPTOMS.reduce((sum, s) => sum + (form.scores[s.id] ?? 0), 0);
  const vasomotor = SYMPTOMS.filter((s) => s.group === "Vasomotor").reduce(
    (sum, s) => sum + (form.scores[s.id] ?? 0),
    0,
  );

  // Estimated menopause age — start from population mean, adjust by inputs
  let estimated = 51.5;
  if (motherAge >= 35 && motherAge <= 60) {
    // mother's age is the single strongest predictor; pull ~60% of the way toward it
    estimated = estimated + (motherAge - estimated) * 0.6;
  }
  if (form.smoking === "current") estimated -= 1.5;
  else if (form.smoking === "former") estimated -= 0.5;
  if (form.surgicalHistory === "ovaries_removed") estimated = Math.min(estimated, age);
  if (form.surgicalHistory === "chemo") estimated -= 2;

  // If cycles are stopping, pull estimate toward current age
  if (form.cycle === "very_irregular") estimated = Math.min(estimated, age + 2);
  if (form.cycle === "skipped_months") estimated = Math.min(estimated, age + 1);
  if (form.cycle === "stopped_under_12") estimated = Math.min(estimated, age + 0.5);
  if (form.cycle === "stopped_12_plus") estimated = Math.min(estimated, age);

  estimated = Math.round(estimated * 10) / 10;
  const low = Math.max(35, Math.round(estimated - 2));
  const high = Math.min(60, Math.round(estimated + 2));

  // Stage estimation
  let stage: string;
  let stageDetail: string;
  if (form.surgicalHistory === "ovaries_removed") {
    stage = "Surgical menopause";
    stageDetail =
      "Removal of both ovaries causes an immediate, abrupt menopause regardless of age. Symptoms can be intense and warrant specialist follow-up.";
  } else if (form.cycle === "stopped_12_plus" || monthsSince >= 12) {
    stage = "Postmenopause";
    stageDetail =
      "You've gone 12+ consecutive months without a period — by definition you're postmenopausal. Focus shifts to long-term bone, heart, and metabolic health.";
  } else if (form.cycle === "stopped_under_12" || (monthsSince > 0 && monthsSince < 12)) {
    stage = "Late perimenopause";
    stageDetail =
      "You're inside the final 12-month window. You can only confirm menopause looking back, once a full year has passed without a period.";
  } else if (form.cycle === "very_irregular" || form.cycle === "skipped_months" || (age >= 45 && symptomScore >= 18)) {
    stage = "Late perimenopause";
    stageDetail =
      "Cycle changes plus a meaningful symptom load point to the later part of the transition. Tracking now is especially useful — patterns shift quickly.";
  } else if (form.cycle === "slightly_irregular" || (age >= 40 && symptomScore >= 10)) {
    stage = "Early perimenopause";
    stageDetail =
      "Subtle cycle shifts and emerging symptoms suggest the transition has started. Hormones fluctuate widely here, so a single blood test rarely tells the whole story.";
  } else if (age < 40 && symptomScore >= 14) {
    stage = "Possible early/premature menopause — see a clinician";
    stageDetail =
      "Significant symptoms before 40 should be evaluated. Primary ovarian insufficiency and other causes are treatable, and early diagnosis matters for bone and heart health.";
  } else {
    stage = "Premenopause";
    stageDetail =
      "Your cycle pattern and symptom load look like premenopause. This is the ideal time to establish a baseline so subtle changes are easy to spot later.";
  }

  // Symptom band
  let symptomBand: string;
  if (symptomScore <= 8) symptomBand = "Low symptom burden";
  else if (symptomScore <= 20) symptomBand = "Moderate symptom burden";
  else symptomBand = "High symptom burden";

  return {
    estimated,
    low,
    high,
    symptomScore,
    vasomotor,
    symptomBand,
    stage,
    stageDetail,
  };
}

function MenopauseAgeCalculatorPage() {
  const [form, setForm] = useState<FormState>({
    age: "",
    motherAge: "",
    smoking: "never",
    surgicalHistory: "none",
    cycle: "regular",
    monthsSincePeriod: "",
    scores: {},
  });
  const [submitted, setSubmitted] = useState(false);

  const result = useMemo(() => (submitted && form.age ? calculate(form) : null), [submitted, form]);

  const setScore = (id: string, value: number) =>
    setForm((prev) => ({ ...prev, scores: { ...prev.scores, [id]: value } }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    // Smooth scroll to result
    if (typeof window !== "undefined") {
      setTimeout(() => {
        document.getElementById("result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, rgba(243,78,84,0.07), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(255,200,210,0.25), transparent 55%), #FFFBF8",
        minHeight: "100vh",
      }}
    >
      <header
        style={{
          borderBottom: "1px solid rgba(75,31,79,0.08)",
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "saturate(140%) blur(8px)",
        }}
      >
        <div
          style={{
            maxWidth: 880,
            margin: "0 auto",
            padding: "14px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
          }}
        >
          <Link
            to="/"
            style={{ fontFamily: "'Gloock', serif", fontSize: 22, color: PLUM, textDecoration: "none" }}
          >
            <span style={{ color: PLUM_DEEP }}>Know</span>
            <span style={{ color: CORAL }}>Meno</span>
          </Link>
          <Link
            to="/sign-up"
            style={{
              fontFamily: "'Lora', serif",
              fontSize: 14,
              background: CORAL,
              color: "#fff",
              padding: "10px 16px",
              borderRadius: 999,
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Start tracking free
          </Link>
        </div>
      </header>

      <main style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 80px" }}>
        <nav aria-label="Breadcrumb" style={{ fontFamily: "'Lora', serif", fontSize: 14, color: MUTED, marginBottom: 20 }}>
          <ol style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", padding: 0, margin: 0 }}>
            <li>
              <Link to="/" style={{ color: MUTED, textDecoration: "underline" }}>Home</Link>
            </li>
            <li aria-hidden="true">›</li>
            <li>
              <Link to="/menopause-guide" style={{ color: MUTED, textDecoration: "underline" }}>Menopause Guide</Link>
            </li>
            <li aria-hidden="true">›</li>
            <li aria-current="page" style={{ color: PLUM_DEEP, fontWeight: 600 }}>Menopause Age Calculator</li>
          </ol>
        </nav>

        <article>
          <h1
            style={{
              fontFamily: "'Gloock', serif",
              fontSize: "clamp(2rem, 5.2vw, 2.9rem)",
              color: PLUM,
              lineHeight: 1.15,
              margin: "0 0 18px",
            }}
          >
            Menopause Age Calculator
          </h1>

          <p style={pStyle}>
            Use this free calculator to estimate where you are in the menopause transition and the
            age range when you're most likely to reach menopause. It combines your age, cycle
            pattern, family history, smoking status, surgical history, and a symptom check inspired
            by the <strong>Greene Climacteric Scale</strong> — a validated 21-item questionnaire
            used in menopause research and clinical practice.
          </p>
          <p style={pStyle}>
            This is an estimate, not a diagnosis. For a fuller picture, see our{" "}
            <Link to="/menopause-guide" style={{ color: "#B82A30", textDecoration: "underline", fontWeight: 600 }}>
              4 stages of menopause guide
            </Link>{" "}
            and bring your results to a clinician.
          </p>

          {/* ---------- Calculator form ---------- */}
          <section
            aria-labelledby="calculator-heading"
            style={{
              marginTop: 28,
              background: "rgba(255,255,255,0.85)",
              border: "1px solid rgba(75,31,79,0.12)",
              borderRadius: 16,
              padding: "24px 22px",
              boxShadow: "0 6px 20px rgba(75,31,79,0.06)",
            }}
          >
            <h2 id="calculator-heading" style={{ ...h2Style, marginTop: 0, fontSize: "clamp(1.4rem, 3vw, 1.7rem)" }}>
              Your details
            </h2>

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                <div style={fieldGroup}>
                  <label htmlFor="age" style={labelStyle}>Your age</label>
                  <input
                    id="age"
                    type="number"
                    min={18}
                    max={80}
                    required
                    value={form.age}
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g. 47"
                  />
                </div>
                <div style={fieldGroup}>
                  <label htmlFor="motherAge" style={labelStyle}>Mother's age at menopause (optional)</label>
                  <input
                    id="motherAge"
                    type="number"
                    min={30}
                    max={65}
                    value={form.motherAge}
                    onChange={(e) => setForm({ ...form, motherAge: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g. 52"
                  />
                </div>
              </div>

              <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                <div style={fieldGroup}>
                  <label htmlFor="smoking" style={labelStyle}>Smoking status</label>
                  <select
                    id="smoking"
                    value={form.smoking}
                    onChange={(e) => setForm({ ...form, smoking: e.target.value as FormState["smoking"] })}
                    style={selectStyle}
                  >
                    <option value="never">Never smoked</option>
                    <option value="former">Former smoker</option>
                    <option value="current">Current smoker</option>
                  </select>
                </div>
                <div style={fieldGroup}>
                  <label htmlFor="surgical" style={labelStyle}>Surgical / medical history</label>
                  <select
                    id="surgical"
                    value={form.surgicalHistory}
                    onChange={(e) => setForm({ ...form, surgicalHistory: e.target.value as FormState["surgicalHistory"] })}
                    style={selectStyle}
                  >
                    <option value="none">None of the below</option>
                    <option value="hysterectomy">Hysterectomy (uterus removed, ovaries intact)</option>
                    <option value="ovaries_removed">Both ovaries removed</option>
                    <option value="chemo">Chemotherapy or pelvic radiation</option>
                  </select>
                </div>
              </div>

              <div style={fieldGroup}>
                <label htmlFor="cycle" style={labelStyle}>Your menstrual cycle</label>
                <select
                  id="cycle"
                  value={form.cycle}
                  onChange={(e) => setForm({ ...form, cycle: e.target.value as CycleRegularity })}
                  style={selectStyle}
                >
                  <option value="regular">Regular — predictable each month</option>
                  <option value="slightly_irregular">Slightly irregular — length varies by a few days</option>
                  <option value="very_irregular">Very irregular — length varies by 7+ days</option>
                  <option value="skipped_months">I've skipped one or more months</option>
                  <option value="stopped_under_12">My periods stopped less than 12 months ago</option>
                  <option value="stopped_12_plus">My periods stopped 12+ months ago</option>
                </select>
              </div>

              <div style={fieldGroup}>
                <label htmlFor="monthsSince" style={labelStyle}>Months since your last period (optional)</label>
                <input
                  id="monthsSince"
                  type="number"
                  min={0}
                  max={120}
                  value={form.monthsSincePeriod}
                  onChange={(e) => setForm({ ...form, monthsSincePeriod: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g. 4"
                />
              </div>

              <h3 style={h3Style}>Symptom check (Greene-inspired)</h3>
              <p style={{ ...pStyle, fontSize: 14, color: MUTED, marginBottom: 16 }}>
                Rate how much each has bothered you in the past month: 0 = none, 1 = mild, 2 = moderate, 3 = severe.
              </p>

              {(["Vasomotor", "Psychological", "Somatic", "Sexual"] as SymptomGroup[]).map((group) => (
                <fieldset
                  key={group}
                  style={{
                    border: "1px solid rgba(75,31,79,0.12)",
                    borderRadius: 12,
                    padding: "14px 16px",
                    margin: "0 0 14px",
                  }}
                >
                  <legend
                    style={{
                      fontFamily: "'Gloock', serif",
                      color: PLUM_DEEP,
                      padding: "0 8px",
                      fontSize: 15,
                    }}
                  >
                    {group}
                  </legend>
                  {SYMPTOMS.filter((s) => s.group === group).map((s) => (
                    <div
                      key={s.id}
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 0",
                        borderTop: "1px dashed rgba(75,31,79,0.08)",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'Lora', serif",
                          fontSize: 15,
                          color: INK,
                          flex: "1 1 200px",
                        }}
                      >
                        {s.label}
                      </span>
                      <div role="radiogroup" aria-label={s.label} style={{ display: "flex", gap: 6 }}>
                        {[0, 1, 2, 3].map((v) => {
                          const selected = (form.scores[s.id] ?? 0) === v;
                          return (
                            <button
                              key={v}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              onClick={() => setScore(s.id, v)}
                              style={{
                                minWidth: 40,
                                padding: "8px 10px",
                                borderRadius: 8,
                                border: `1px solid ${selected ? CORAL : "rgba(75,31,79,0.2)"}`,
                                background: selected ? CORAL : "#fff",
                                color: selected ? "#fff" : PLUM_DEEP,
                                fontFamily: "'Lora', serif",
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                              title={SEVERITY_LABELS[v]}
                            >
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </fieldset>
              ))}

              <button
                type="submit"
                style={{
                  marginTop: 16,
                  width: "100%",
                  background: CORAL,
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  padding: "14px 22px",
                  fontFamily: "'Lora', serif",
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 6px 14px rgba(243,78,84,0.25)",
                }}
              >
                Calculate my estimate
              </button>
            </form>
          </section>

          {/* ---------- Result ---------- */}
          {result && (
            <section
              id="result"
              aria-live="polite"
              style={{
                marginTop: 28,
                background: "linear-gradient(135deg, rgba(45,14,67,0.97), rgba(75,31,79,0.97))",
                color: "#fff",
                borderRadius: 16,
                padding: "26px 24px",
                boxShadow: "0 8px 24px rgba(45,14,67,0.18)",
              }}
            >
              <h2 style={{ ...h2Style, marginTop: 0, color: "#fff" }}>Your estimate</h2>

              <div
                style={{
                  display: "grid",
                  gap: 14,
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: 12,
                    padding: "16px 18px",
                  }}
                >
                  <div style={{ fontFamily: "'Lora', serif", fontSize: 13, opacity: 0.8, textTransform: "uppercase", letterSpacing: 1 }}>
                    Estimated stage
                  </div>
                  <div style={{ fontFamily: "'Gloock', serif", fontSize: 22, marginTop: 6, color: "#FFD1D7" }}>
                    {result.stage}
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: 12,
                    padding: "16px 18px",
                  }}
                >
                  <div style={{ fontFamily: "'Lora', serif", fontSize: 13, opacity: 0.8, textTransform: "uppercase", letterSpacing: 1 }}>
                    Likely menopause age range
                  </div>
                  <div style={{ fontFamily: "'Gloock', serif", fontSize: 22, marginTop: 6, color: "#FFD1D7" }}>
                    {result.low}–{result.high} <span style={{ fontSize: 14, opacity: 0.8 }}>(est. {result.estimated})</span>
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: 12,
                    padding: "16px 18px",
                  }}
                >
                  <div style={{ fontFamily: "'Lora', serif", fontSize: 13, opacity: 0.8, textTransform: "uppercase", letterSpacing: 1 }}>
                    Symptom score
                  </div>
                  <div style={{ fontFamily: "'Gloock', serif", fontSize: 22, marginTop: 6, color: "#FFD1D7" }}>
                    {result.symptomScore} / 45 — {result.symptomBand}
                  </div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: 13, marginTop: 6, opacity: 0.85 }}>
                    Vasomotor sub-score: {result.vasomotor} / 9
                  </div>
                </div>
              </div>

              <p style={{ fontFamily: "'Lora', serif", fontSize: 15, lineHeight: 1.7, margin: "0 0 14px" }}>
                {result.stageDetail}
              </p>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
                <Link
                  to="/sign-up"
                  style={{
                    background: CORAL,
                    color: "#fff",
                    padding: "12px 18px",
                    borderRadius: 999,
                    fontFamily: "'Lora', serif",
                    fontSize: 14,
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                >
                  Track these symptoms with KnowMeno
                </Link>
                <Link
                  to="/menopause-guide"
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    padding: "12px 18px",
                    borderRadius: 999,
                    fontFamily: "'Lora', serif",
                    fontSize: 14,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  Read the full menopause guide
                </Link>
              </div>
            </section>
          )}

          {/* ---------- How it works ---------- */}
          <section>
            <h2 style={h2Style}>How this calculator works</h2>
            <p style={pStyle}>
              The calculator combines four well-established signals used by menopause specialists:
            </p>
            <ul style={{ ...pStyle, paddingLeft: "1.25rem" }}>
              <li>
                <strong>Your age</strong> — the strongest contextual factor. Average natural
                menopause sits around 51–52, with most women reaching it between 45 and 55.
              </li>
              <li>
                <strong>Cycle pattern</strong> — the Stages of Reproductive Aging Workshop
                (STRAW+10) framework uses cycle changes as a primary marker of where you are in the
                transition.
              </li>
              <li>
                <strong>Family history &amp; lifestyle</strong> — mother's age at menopause is the
                single strongest individual predictor; current smoking shifts menopause ~1–2 years
                earlier on average.
              </li>
              <li>
                <strong>Greene Climacteric Scale–inspired symptoms</strong> — 15 items across
                vasomotor, psychological, somatic, and sexual domains, scored 0–3, summed to give a
                symptom-burden band that helps locate you in the transition.
              </li>
            </ul>
            <p style={pStyle}>
              The output is a stage estimate, a symptom-burden band, and a likely menopause age
              range. Surgical menopause (both ovaries removed) is treated as an immediate menopause
              regardless of age, and significant symptoms before 40 are flagged for clinician
              follow-up to rule out primary ovarian insufficiency.
            </p>
          </section>

          {/* ---------- FAQ ---------- */}
          <section>
            <h2 style={h2Style}>Frequently asked questions</h2>
            {faqItems.map((item) => (
              <div key={item.q} style={{ marginBottom: 18 }}>
                <h3 style={h3Style}>{item.q}</h3>
                <p style={pStyle}>{item.a}</p>
              </div>
            ))}
          </section>

          <p
            style={{
              fontFamily: "'Lora', serif",
              fontSize: 13,
              lineHeight: 1.65,
              color: MUTED,
              marginTop: 40,
              padding: "16px 18px",
              background: "rgba(75,31,79,0.04)",
              border: "1px solid rgba(75,31,79,0.08)",
              borderRadius: 10,
            }}
          >
            This calculator is for general educational purposes only and is not medical advice. It
            is not intended to diagnose, treat, cure, or prevent any condition and is not a
            substitute for professional medical advice, diagnosis, or treatment. Menopause symptoms
            can overlap with other conditions, and individual circumstances vary. Always consult a
            qualified healthcare provider about your health, symptoms, or treatment options.
          </p>
        </article>
      </main>
    </div>
  );
}
